import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Dropdown, DropdownFilterEvent, DropdownChangeEvent, DropdownProps } from 'primereact/dropdown';
import { SelectItem } from 'primereact/selectitem';
import { Typography } from '../Typography';
import { InfoTooltip } from "../Tooltip/InfoTooltip";
import SelectIcon from '../../assets/select.svg';
import SelectOpenIcon from '../../assets/selectOpen.svg';

interface FormSelectProps {
  name: string;
  value: string;
  items: SelectItem[];
  onChange: (event: DropdownChangeEvent) => void;
  onFilter?: (event: DropdownFilterEvent) => void;
  placeholder?: string;
  filterPlaceholder?: string;
  filter?: boolean;
  resetFilterOnHide?: boolean;
  editable?: boolean;
  label?: React.ReactNode;
  tooltip?: React.ReactNode;
  panelFooterTemplate?: React.ReactNode;
  itemTemplate?: DropdownProps['itemTemplate'];
  valueTemplate?: DropdownProps['valueTemplate'];
  addAsNew?: boolean;
  text?: string;
}

export const Select: React.FC<FormSelectProps> = props => {
  const [searchValue, setSearchValue] = useState('');
  const [hasResults, setHasResults] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef<Dropdown>(null);
  const { addAsNew = false, text = 'Add as new' } = props;

  const hasLetter = (value: string) =>
    //@ts-ignore

    /\p{L}/u.test(value);

  const handleClick = () => {
    if (!isOpen) {
      dropdownRef.current?.show?.();
      setIsOpen(true);
    }
  };

  const handleChange = (e: DropdownChangeEvent) => {
    props.onChange?.(e);
    setSearchValue('');
    dropdownRef.current?.hide?.();
    setIsOpen(false);
  };

  const handleFilter = (e: DropdownFilterEvent) => {
    const searchText = e.filter || '';
    setSearchValue(searchText);

    const filtered = props.items.filter(item =>
      (item.label as string).toLowerCase().includes(searchText.toLowerCase())
    );

    setHasResults(filtered.length > 0);
    props.onFilter?.(e);
  };

  return (
    <Container>
      {!!props.label && (
        <Label>
          <Typography variant="body6">
            <label htmlFor={props.name}>{props.label}</label>
          </Typography>
          {props.tooltip && (
            <InfoTooltip
              name={`select-tooltip-${props.name}`}
              tooltip={props.tooltip}
            />
          )}
        </Label>
      )}

      <StyledDropdown
        ref={dropdownRef}
        appendTo={null}
        editable={props.editable}
        value={props.value}
        options={props.items}
        placeholder={props.placeholder}
        filter
        filterPlaceholder={props.filterPlaceholder || 'Search...'}
        inputId={props.name}
        itemTemplate={props.itemTemplate}
        valueTemplate={props.valueTemplate}
        dropdownIcon={hasResults ? <SelectIcon /> : <SelectOpenIcon />}
        onClick={handleClick}
        onChange={handleChange}
        onFilter={handleFilter}
        resetFilterOnHide={props.resetFilterOnHide}
        onHide={() => setIsOpen(false)}
        panelFooterTemplate={() =>
          addAsNew && (
            <Box>
              <Button
                disabled={hasResults || !hasLetter(searchValue)}
                onClick={(e) => {
                  e.preventDefault();
                  if (!hasLetter(searchValue)) return;

                  props.onChange?.({
                    originalEvent: null as any,
                    value: searchValue,
                    target: { name: props.name, value: searchValue },
                  } as DropdownChangeEvent);

                  setSearchValue('');
                  dropdownRef.current?.hide?.();
                  setIsOpen(false);
                }}
              >
                {text}
              </Button>
            </Box>
          )
        }
      />
    </Container>
  );
};

// Styled components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledDropdown = styled(Dropdown)`
  border-radius: 10px;

  .p-dropdown-item-label {
    width: 100% !important;
  }

  /* Mobile: keep dropdown panel compact inside modals */
  @media (max-width: 768px) {
    .p-dropdown-panel {
      max-height: 60dvh;
    }

    .p-dropdown-items-wrapper {
      max-height: calc(60dvh - 56px); /* account for filter + footer */
    }

    .p-dropdown-filter {
      padding: 10px 12px;
    }

    .p-dropdown-filter-container input {
      height: 40px;
      border-radius: 10px;
    }

    .p-dropdown-item {
      padding: 10px 12px;
    }
  }

  .p-dropdown-empty-message {
    display: flex !important;
    height: 173px !important;
    justify-content: center !important;
    align-items: center !important;
    text-align: center !important;
    color: #999999 !important;
    font-style: italic !important;
    width: 100% !important;
    z-index: 9999 !important;
  }

  @media (max-width: 768px) {
    .p-dropdown-empty-message {
      height: 120px !important;
    }
  }
`;

const Label = styled.div`
  display: flex;
  gap: 4px;
`;

const Box = styled.div`
  width: 100%;
  height: 68px;
  border-top: 1px solid #00000033;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 11px;

  /* Mobile: smaller footer area inside dropdown */
  @media (max-width: 768px) {
    height: 52px;
    padding-right: 10px;
  }
`;

const Button = styled.button<{ disabled?: boolean }>`
  padding: 8px 20px 9px 21px;
  height: 35px;
  border-radius: 8px;
  background: ${({ disabled }) => (disabled ? '#6777FF80' : '#6777FF')};
  font-family: SF Pro Display;
  font-weight: 600;
  font-style: Semibold;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0%;
  color: #FFFFFF;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  border: none;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  transition: background 0.2s ease, opacity 0.2s ease;

  /* Mobile: smaller "Add as new" button */
  @media (max-width: 768px) {
    height: 30px;
    padding: 6px 14px;
    font-size: 12px;
    line-height: 16px;
    border-radius: 7px;
  }
`;
